import { Suspense, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import { RouteFallback, SkipLink } from '@/components/app';
import { BottomNav } from '@/components/layout/BottomNav';
import { BreakingTicker } from '@/components/layout/BreakingTicker';
import { CategoryNav, MobileSectionsBar } from '@/components/layout/CategoryNav';
import { Masthead } from '@/components/layout/Masthead';
import { PolicyFooter } from '@/components/layout/PolicyFooter';
import { useI18n } from '@/i18n';
import { useAuth } from '@/stores/auth';

/**
 * Public reader shell: masthead (scrolls away) · category nav (sticky) ·
 * breaking ticker · <main id="main"> · footer.
 *
 * Bilingual (§16 open item 3, decided in favour of both): chrome switches
 * between Telugu and English, while article headlines fall back to Telugu when
 * no English version exists (`useI18n().pick`). Category names come from the
 * database in both languages, so the nav translates without a hardcoded map.
 *
 * The shell owns the one <main> landmark; pages render PageContainer roots.
 */
export default function PublicLayout() {
  const { language } = useI18n();
  const authStatus = useAuth((s) => s.status);
  const bootstrap = useAuth((s) => s.bootstrap);
  // The WOW home page carries only what the concept pitch lists; the breaking
  // ticker is a news-desk element, so it shows on every other page instead.
  const isHome = useLocation().pathname === '/';

  // Keep <html lang> correct on first paint as well as after a switch.
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  // Resolve the reader's session so the masthead shows profile vs sign-in.
  useEffect(() => {
    if (authStatus === 'idle') void bootstrap();
  }, [authStatus, bootstrap]);

  return (
    <div className="flex min-h-screen flex-col bg-canvas">
      <SkipLink />
      {/* Only the masthead is the banner. The sticky nav sits directly in the
          root column so its containing block is the full page, not a boxed
          header that would scroll away with the masthead. */}
      {/* Phones: every section in a marquee strip above the wordmark. */}
      <MobileSectionsBar />
      <header>
        <Masthead />
      </header>
      <CategoryNav />
      {isHome ? null : <BreakingTicker />}

      <main id="main" tabIndex={-1} className="flex-1 outline-none">
        <Suspense fallback={<RouteFallback />}>
          <Outlet />
        </Suspense>
      </main>

      <PolicyFooter />
      <BottomNav />
    </div>
  );
}
